"use client";

/**
 * Hoverable/keyboard-friendly megamenu for the "Categorías" navbar tab.
 *
 * Open behaviour:
 *   - Opens on `mouseenter` / `focus` of the trigger or panel.
 *   - Closes on `mouseleave` with a small grace delay so the user can
 *     diagonally cross from trigger to panel without the menu closing.
 *   - Closes on `Escape` and on `pointerdown` outside.
 *
 * On screens < lg the trigger collapses to a plain link to /categorias —
 * the mobile drawer handles the full taxonomy already.
 *
 * The panel itself is anchored to the trigger with absolute positioning;
 * it lives inside the navbar so existing focus management keeps working.
 */
import { useEffect, useId, useRef, useState } from "react";
import Link from "next/link";
import { ChevronRight } from "lucide-react";
import type { MegamenuCategory } from "@/lib/categories-menu";
import { CategoryIcon } from "@/components/ui/category-icon";
import { cn } from "@/lib/utils";

/** Visual fallback when the Category table is empty so the panel never
 *  renders as a plain link. Once the admin seeds taxonomy, the DB list
 *  takes over. */
const FALLBACK_CATEGORIES: readonly MegamenuCategory[] = [
  { slug: "supermercado", name: "Supermercado", emoji: "🛒", subcategories: [] },
  { slug: "lacteos", name: "Lácteos", emoji: "🥛", subcategories: [] },
  { slug: "celulares", name: "Celulares", emoji: "📱", subcategories: [] },
  { slug: "electronica", name: "Electrónica", emoji: "📺", subcategories: [] },
  { slug: "computacion", name: "Computación", emoji: "💻", subcategories: [] },
  { slug: "belleza", name: "Belleza", emoji: "✨", subcategories: [] },
  { slug: "medicamentos", name: "Medicamentos", emoji: "💊", subcategories: [] },
  { slug: "hogar", name: "Hogar", emoji: "🏠", subcategories: [] },
  { slug: "limpieza", name: "Limpieza", emoji: "🧴", subcategories: [] },
  { slug: "bebidas", name: "Bebidas", emoji: "🍷", subcategories: [] },
  { slug: "deportes", name: "Deportes", emoji: "🏋️", subcategories: [] },
  { slug: "ropa", name: "Ropa", emoji: "👕", subcategories: [] },
];

interface Props {
  categories: readonly MegamenuCategory[];
  /** Optional legacy ad slot rendered inside the right column. */
  adSlot?: React.ReactNode;
  /** Category-aligned 300×600 ad slots. */
  verticalAdSlots?: readonly React.ReactNode[];
  /** Category-aligned horizontal ad slots. */
  horizontalAdSlots?: readonly React.ReactNode[];
  /** href of the "Categorías" page used as the trigger link. */
  href?: string;
  active?: boolean;
  triggerClassName?: string;
  children: React.ReactNode;
}

const CLOSE_DELAY_MS = 120;

export function CategoriesMegamenu({
  categories,
  adSlot,
  verticalAdSlots,
  horizontalAdSlots,
  href = "/categorias",
  active,
  triggerClassName,
  children,
}: Props) {
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  const list = categories.length > 0 ? categories : FALLBACK_CATEGORIES;

  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };
  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };
  const openNow = () => {
    cancelClose();
    setOpen(true);
  };

  // Outside click + Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const activeCat = list[Math.min(activeIdx, list.length - 1)];
  const activeSlotIdx = Math.min(activeIdx, list.length - 1);
  const verticalAd = verticalAdSlots?.[activeSlotIdx] ?? adSlot;
  const horizontalAd = horizontalAdSlots?.[activeSlotIdx] ?? null;
  const hasRightRail = Boolean(verticalAd);

  return (
    <div
      ref={wrapperRef}
      className="relative"
      onMouseEnter={openNow}
      onMouseLeave={scheduleClose}
      onFocus={openNow}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) scheduleClose();
      }}
    >
      <Link
        href={href}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={panelId}
        aria-current={active ? "page" : undefined}
        className={triggerClassName}
        onClick={(e) => {
          // Plain left-click toggles the panel instead of navigating —
          // matches typical megamenu UX while keeping the <a href> for
          // crawlers, cmd/middle-click "open in new tab", and screen
          // readers. The "Ver todas las categorías" link inside the panel
          // sends users to /categorias when they actually want it.
          const modifier = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
          if (e.button === 0 && !modifier) {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
          } else if (e.key === "ArrowDown" && !open) {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        {children}
      </Link>

      {open ? (
        <div
          id={panelId}
          role="menu"
          aria-label="Categorías"
          className={cn(
            "hidden lg:block absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50",
            "w-[min(1080px,calc(100vw-2rem))] rounded-2xl border bg-background shadow-2xl shadow-black/10",
            "p-3 sm:p-4"
          )}
          onMouseEnter={openNow}
          onMouseLeave={scheduleClose}
        >
          <div className={cn("grid gap-3", hasRightRail ? "grid-cols-[220px_1fr_300px]" : "grid-cols-[220px_1fr]")}>
            {/* Categories column */}
            <ul className="space-y-0.5 max-h-[420px] overflow-y-auto pr-1">
              {list.map((c, i) => (
                <li key={c.slug}>
                  <Link
                    href={`/categoria/${c.slug}`}
                    onMouseEnter={() => setActiveIdx(i)}
                    onFocus={() => setActiveIdx(i)}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                      i === activeIdx
                        ? "bg-muted text-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                    )}
                  >
                    <CategoryIcon value={c.emoji} className="h-4 w-4 shrink-0 text-base" />
                    <span className="flex-1 min-w-0 truncate">{c.name}</span>
                    <ChevronRight className="w-3.5 h-3.5 opacity-50" />
                  </Link>
                </li>
              ))}
            </ul>

            {/* Subcategories of active column */}
            <div className="min-w-0">
              <h3 className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2">
                {activeCat.name}
              </h3>
              {activeCat.subcategories.length > 0 ? (
                <ul className="grid grid-cols-2 gap-1.5">
                  {activeCat.subcategories.map((s) => (
                    <li key={s.slug}>
                      <Link
                        href={`/categoria/${activeCat.slug}?sub=${s.slug}`}
                        className="block rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors truncate"
                      >
                        {s.emoji ? <CategoryIcon value={s.emoji} className="mr-1 inline-flex h-4 w-4 align-[-0.15em] text-sm" /> : null}
                        {s.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-muted-foreground">Sin subcategorías.</p>
              )}
              <Link
                href={`/categoria/${activeCat.slug}`}
                className="inline-flex items-center gap-1 mt-3 text-xs font-semibold text-primary hover:underline"
              >
                Ver todo en {activeCat.name} <ChevronRight className="w-3 h-3" />
              </Link>
            </div>

            {/* Optional ad column */}
            {verticalAd ? <div className="hidden xl:block">{verticalAd}</div> : null}
          </div>

          <div className="border-t mt-3 pt-3 space-y-3">
            {horizontalAd ? <div>{horizontalAd}</div> : null}
            <div className="flex items-center justify-between">
              <Link
                href="/categorias"
                className="text-xs font-semibold text-primary hover:underline"
              >
                Ver todas las categorías →
              </Link>
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                {list.length} categorías
              </span>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
